import { BALL_RADIUS } from '../types';
import type { TableDefinition, WallPart } from '../tables/definition';

export type { PhysicsEvent } from './worldRuntime';
export type PinballWorld = import('./worldRuntime').PinballWorld;

type PhysicsRuntime = typeof import('./worldRuntime');

const SAFETY_ENVELOPE_PREFIX = '__physics-safety-';
const MIN_ROLLOVER_SENSOR_RADIUS = BALL_RADIUS * 1.15;

export function withPhysicsSafetyEnvelope(definition: TableDefinition): TableDefinition {
  const inset = BALL_RADIUS * 1.25;
  const safetyWalls: WallPart[] = [
    {
      id: `${SAFETY_ENVELOPE_PREFIX}left`,
      kind: 'wall',
      scoreValue: 0,
      thickness: 1,
      points: [
        { x: -inset, y: -inset },
        { x: -inset, y: definition.board.height },
      ],
    },
    {
      id: `${SAFETY_ENVELOPE_PREFIX}right`,
      kind: 'wall',
      scoreValue: 0,
      thickness: 1,
      points: [
        { x: definition.board.width + inset, y: -inset },
        { x: definition.board.width + inset, y: definition.board.height },
      ],
    },
    {
      id: `${SAFETY_ENVELOPE_PREFIX}top`,
      kind: 'wall',
      scoreValue: 0,
      thickness: 1,
      points: [
        { x: -inset, y: -inset },
        { x: definition.board.width + inset, y: -inset },
      ],
    },
  ];

  const existingIds = new Set(definition.parts.map((part) => part.id));
  return {
    ...definition,
    parts: [
      ...definition.parts,
      ...safetyWalls.filter((wall) => !existingIds.has(wall.id)),
    ],
  };
}

/**
 * Sensor-only reliability tuning for the physics copy. Rollover artwork can stay
 * compact while the invisible trigger remains large enough for a 240 Hz ball to
 * register a fast lane crossing consistently. Never mutate the authored table:
 * renderers/editor/rules keep the original dimensions.
 */
export function withReliableTriggerSensors(definition: TableDefinition): TableDefinition {
  let changed = false;
  const parts = definition.parts.map((part) => {
    if (part.kind !== 'rollover' || part.radius >= MIN_ROLLOVER_SENSOR_RADIUS) return part;
    changed = true;
    return { ...part, radius: MIN_ROLLOVER_SENSOR_RADIUS };
  });
  return changed ? { ...definition, parts } : definition;
}

export function createRetryableModuleLoader<T>(load: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | null = null;

  return () => {
    if (pending) return pending;

    let attempt: Promise<T>;
    try {
      attempt = Promise.resolve(load());
    } catch (error) {
      // Although native import() normally reports failures by rejecting its
      // promise, bundler/runtime shims can throw synchronously before returning
      // one. Normalize that path to a rejected promise so callers keep the same
      // async contract and the retry state can be cleared consistently.
      attempt = Promise.reject(error);
    }

    pending = attempt;
    void attempt.catch(() => {
      // A transient chunk/network failure must not poison every later attempt.
      // Clear only the matching in-flight promise so concurrent callers still
      // share one load while a successful resolution remains cached forever.
      if (pending === attempt) pending = null;
    });
    return attempt;
  };
}

const loadPhysicsRuntime = createRetryableModuleLoader<PhysicsRuntime>(() => import('./worldRuntime'));

/** Warm the physics chunk without constructing a table world yet. */
export async function preloadPhysicsRuntime(): Promise<void> {
  await loadPhysicsRuntime();
}

/**
 * Value-side compatibility facade. Existing engine/tests can continue calling
 * PinballWorld.create(), while the full Rapier implementation stays out of the
 * critical homepage module graph until a table is actually prepared.
 */
export const PinballWorld = {
  async create(definition: TableDefinition): Promise<PinballWorld> {
    const runtime = await loadPhysicsRuntime();
    const guarded = withPhysicsSafetyEnvelope(definition);
    return runtime.PinballWorld.create(withReliableTriggerSensors(guarded));
  },
} as const;
